import { useEffect, useMemo, useState } from 'react';
import { useTenantStore } from '../../stores/tenantStore';
import { useAuthStore } from '../../stores/authStore';
import tenantService from '../../services/tenant.service';
import channelService, { type Channel } from '../../services/channel.service';
import { USER_TYPES } from '../../utils/constants';

/**
 * Topbar scope picker, rendered for the two user types that have no tenant of
 * their own: SuperAdmin (type 1) and ChannelAdmin (type 5).
 *
 * The option set always comes from `GET /tenants/select`, which is already
 * scope-aware server-side — a ChannelAdmin receives only their channels'
 * tenants without the client filtering anything. Channel data is used purely
 * to group the options visually; if it is unavailable (403, or a deployment
 * with no channels at all) the picker falls back to the flat list it has
 * always rendered, so nothing changes for existing SuperAdmins.
 */
const TenantSelector = () => {
  const { tenants, selectedTenantId, setTenants, setSelectedTenantId } = useTenantStore();
  const user = useAuthStore((s) => s.user);
  const isChannelAdmin = user?.type === USER_TYPES.CHANNEL_ADMIN;

  // Channel names, for the <optgroup> labels. The tenant→channel mapping itself
  // travels on each tenant (`t.channelId` from /tenants/select), so this is the
  // only extra fetch, and it never needs to react to tenant changes — a new
  // tenant carries its own channelId in the store list.
  const [channels, setChannels] = useState<Channel[]>([]);

  // Fetch tenants on mount
  useEffect(() => {
    tenantService.getSelect()
      .then((data) => {
        // Filter out 'default' tenant like V1 does
        const filteredTenants = data.filter((t) => t.name !== 'default');
        setTenants(filteredTenants);
      })
      .catch((err) => console.error('Failed to fetch tenants:', err));
  }, [setTenants]);

  // Channel names for grouping — best effort, background (no loading overlay),
  // and a failure just falls back to a flat list. Fetched once: channel names
  // rarely change within a session, and the tenant→channel edges live on the
  // tenant objects, so nothing here has to track tenant creation.
  useEffect(() => {
    let cancelled = false;
    channelService.listAll(undefined, { showLoading: false })
      .then((items) => { if (!cancelled) setChannels(items); })
      .catch(() => { /* flat list */ });
    return () => { cancelled = true; };
  }, []);

  // Group the (authoritative) select options by each tenant's own channelId.
  // Tenants with no channel — legacy "direct" tenants predating the layer —
  // collect into a trailing group rather than being hidden.
  const groups = useMemo(() => {
    if (channels.length === 0) return null;

    const byChannel = channels
      .map((c) => ({
        label: c.name,
        items: tenants.filter((t) => t.channelId === c.id),
      }))
      .filter((g) => g.items.length > 0);

    const known = new Set(byChannel.flatMap((g) => g.items.map((t) => t.id)));
    const direct = tenants.filter((t) => !known.has(t.id));
    if (direct.length > 0) byChannel.push({ label: 'Direct (no channel)', items: direct });

    // Nothing actually resolved to a channel — render flat instead of a
    // single misleading "Direct" bucket holding everything.
    return byChannel.length > 1 || known.size > 0 ? byChannel : null;
  }, [channels, tenants]);

  const handleTenantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTenantId = e.target.value;
    setSelectedTenantId(newTenantId);
    // Redirect (not just reload) — the current route may be a tenant-scoped
    // detail page (e.g. /edges/:id) whose resource belongs to the previous
    // tenant. Reloading keeps the user on a stale URL where all metrics read
    // empty. Navigating also forces a full reload so every in-flight fetch
    // uses the new tenant filter.
    //
    // A ChannelAdmin clearing their selection has no meaningful dashboard —
    // their "no tenant" home is the channel overview.
    // The channels overview lives at /admin/channels (not /channels, which 404s).
    // Keep this in step with the guards in routes/index.tsx.
    const destination = !newTenantId && isChannelAdmin ? '/admin/channels' : '/dashboard';
    window.location.href = destination;
  };

  const renderOption = (tenant: { id: string; name: string }) => (
    <option key={tenant.id} value={tenant.id}>
      {tenant.name}
    </option>
  );

  return (
    <div className="flex items-center space-x-2">
      <label htmlFor="tenant-select" className="text-sm font-medium text-gray-700">
        Tenant:
      </label>
      <select
        id="tenant-select"
        value={selectedTenantId}
        onChange={handleTenantChange}
        className="input-base py-1 px-2 text-sm w-40"
      >
        <option value="">{isChannelAdmin ? 'All my tenants' : 'All Tenants'}</option>
        {groups
          ? groups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.items.map(renderOption)}
              </optgroup>
            ))
          : tenants.map(renderOption)}
      </select>
    </div>
  );
};

export default TenantSelector;
